import type { BoxCollider, FloorRegion } from './collision';

/**
 * B-2 "Enterable interiors" (PLAN-CITY2 §3) — a basement vestibule reached
 * from a hatch inside the utility tunnel (`world/layout.ts`'s
 * `BASEMENT_HATCH`), deliberately "doubling as [Wave] D-1's entrance" per
 * the plan's own wording: this session builds only the small teleport-only
 * antechamber + a sealed inner door foreshadowing the real Rat King den,
 * matching PLAN-CITY §0.8's own "no completely seamless city" idiom already
 * used for `SUBWAY_GATE`/`CORONA_EXIT_GATE` — Wave D later REPLACES the
 * sealed door with the real den, it does not need to touch this room's own
 * entrance/vestibule geometry to do so.
 *
 * Unlike `interiorLaundromat.ts`/`interior.ts` (both walk-through storefront
 * doors on the exterior block face, where a bodega-style auto-walk trigger
 * is the right UX — nothing else in either building's south wall is a
 * through-route), this hatch sits ALONG the tunnel's own main corridor to
 * `HIDDEN_STASH_MARKER` — an unconditional walk-over auto-trigger there
 * would force a detour into the basement on every trip to the stash, purely
 * annoying rather than a genuine door. This room is therefore reached by a
 * plain INTERACT button both ways (`enter_basement`/`exit_basement`,
 * world/interact.ts), the same idiom `heal`/`stash`/`craft`/`hidden_stash`
 * already use for "a deliberate choice to stop and interact," not a walk-
 * through — so it needs no doorway-threshold/stoop machinery at all, only a
 * fully enclosed room.
 *
 * `BASEMENT_ORIGIN` (x=360) sits 110m from `LAUNDROMAT_ORIGIN` (x=250) and
 * 220m from `INTERIOR_ORIGIN` (x=140) — both comfortably past every room's
 * own `>50`-from-origin self-healing-invariant threshold (main.ts), the
 * same mutual-isolation property `interiorLaundromat.ts`'s own header
 * comment establishes between itself and the bodega.
 */
export const BASEMENT_ORIGIN = { x: 360, z: 0 };

const HALF_WIDTH = 2.5; // X
const HALF_DEPTH = 2.5; // Z
export const BASEMENT_ROOM = {
  minX: BASEMENT_ORIGIN.x - HALF_WIDTH,
  maxX: BASEMENT_ORIGIN.x + HALF_WIDTH,
  minZ: BASEMENT_ORIGIN.z - HALF_DEPTH,
  maxZ: BASEMENT_ORIGIN.z + HALF_DEPTH,
  height: 2.4, // a touch lower than the bodega/laundromat's 2.6m — a cramped basement feel — still clears PLAYER_TOTAL_HEIGHT (1.4) with a full meter of headroom
};

/**
 * Same OrbitCamera-vs-small-room derivation as `INTERIOR_CAMERA_MAX_DISTANCE`
 * (interior.ts)/`LAUNDROMAT_CAMERA_MAX_DISTANCE` (interiorLaundromat.ts): at
 * pitch 0.55rad, a `d` pull-back puts the camera `1.0 + d*sin(0.55)` up and
 * `d*cos(0.55)` out. At d=2.3: ~2.20m up (clears this room's LOWER 2.4m
 * ceiling by 0.20m — checked against this room's own smaller number, not
 * assumed from the other two rooms' larger ceilings) and ~1.96m out (clears
 * this room's 2.5m half-extents by 0.54m).
 */
export const BASEMENT_CAMERA_MAX_DISTANCE = 2.3;

/** Just inside the (south) wall nearest the tunnel hatch — where `enter_basement` lands the player, facing into the small room. */
export const BASEMENT_SPAWN = { x: BASEMENT_ORIGIN.x, z: BASEMENT_ROOM.maxZ - 0.8 };

/** The `exit_basement` interactable ("CLIMB BACK UP") — right by BASEMENT_SPAWN, same "the way out sits by where you came in" reasoning as every other interior's own exit marker. */
export const BASEMENT_UP_MARKER = { x: BASEMENT_ORIGIN.x, z: BASEMENT_ROOM.maxZ - 0.6 };

/**
 * A sealed door on the far (north) wall — the D-1 foreshadowing detail
 * (this room's own "one small reason to enter," per PLAN-CITY2 §3's
 * "flavor, NPC-less detail" option). Reuses the EXISTING `locked_seam`
 * InteractAction verbatim (world/interact.ts already has the plumbing for
 * "a real door, shows a message, goes nowhere yet" — `SUBWAY_GATE`/
 * `CORONA_EXIT_GATE` are the precedent), so no new action type or
 * main.ts case is needed for this room specifically.
 */
export const BASEMENT_SEALED_DOOR_MARKER = { x: BASEMENT_ORIGIN.x, z: BASEMENT_ROOM.minZ + 0.8 };

export const BASEMENT_FLOOR: FloorRegion = {
  kind: 'flat',
  minX: BASEMENT_ROOM.minX,
  maxX: BASEMENT_ROOM.maxX,
  minZ: BASEMENT_ROOM.minZ,
  maxZ: BASEMENT_ROOM.maxZ,
  y: 0,
  priority: 1,
};

/** Four FULLY SOLID walls — no doorway gap (see this file's own header comment for why this room is button-only, not walk-through). Same segment idiom as `interiorWalls()`/`laundromatWalls()`. */
export function basementWalls(): BoxCollider[] {
  const t = 0.4;
  const r = BASEMENT_ROOM;
  return [
    { minX: r.minX, maxX: r.maxX, minZ: r.minZ, maxZ: r.minZ + t, topY: r.height, bottomY: 0 }, // north (sealed-door wall)
    { minX: r.minX, maxX: r.maxX, minZ: r.maxZ - t, maxZ: r.maxZ, topY: r.height, bottomY: 0 }, // south (spawn/exit wall)
    { minX: r.minX, maxX: r.minX + t, minZ: r.minZ, maxZ: r.maxZ, topY: r.height, bottomY: 0 }, // west
    { minX: r.maxX - t, maxX: r.maxX, minZ: r.minZ, maxZ: r.maxZ, topY: r.height, bottomY: 0 }, // east
  ];
}
